import { useEffect, useMemo, useState } from "react";
import { useAuthContext } from "../../contexts/AuthContext";
import StatCard from "../../components/StatCard";
import Card from "../../components/Card";
import {
  AnalyticsRange,
  CompletionPoint,
  CourseAnalytics,
  InstructorAnalyticsSummary,
  RevenuePoint,
  TopCourseSalesPoint,
  loadInstructorAnalytics,
} from "../../services/analyticsService";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar,
} from "recharts";
import { BookOpen, Users, DollarSign, Target, TrendingUp, Trophy, Lightbulb, Rocket, MessageCircle, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

export default function Analytics() {
  const { user } = useAuthContext();
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState<AnalyticsRange>("30d");
  const [stats, setStats] = useState<InstructorAnalyticsSummary>({
    totalCourses: 0,
    totalStudents: 0,
    totalRevenue: 0,
    avgCompletionRate: 0,
    topCoursesByRevenue: [],
    topCoursesByStudents: [],
  });
  const [courseStats, setCourseStats] = useState<CourseAnalytics[]>([]);
  const [revenueSeries, setRevenueSeries] = useState<RevenuePoint[]>([]);
  const [completionSeries, setCompletionSeries] = useState<CompletionPoint[]>([]);
  const [topCourseSales, setTopCourseSales] = useState<TopCourseSalesPoint[]>([]);
  const rangeLabel = useMemo(() => {
    if (range === "7d") return "7 วัน";
    if (range === "30d") return "30 วัน";
    if (range === "90d") return "90 วัน";
    return "ทั้งหมด";
  }, [range]);

  const hasRevenueData = useMemo(() => {
    return revenueSeries.some((p) => Number(p.revenue) > 0);
  }, [revenueSeries]);

  const hasCompletionData = useMemo(() => {
    return completionSeries.some((p) => Number(p.completionRate) > 0);
  }, [completionSeries]);

  const hasTopSalesData = useMemo(() => {
    return topCourseSales.some((p) => Number(p.revenue) > 0 || Number(p.sales) > 0);
  }, [topCourseSales]);

  const formatXAxisLabel = useMemo(() => {
    if (range === "all") {
      return (value: any) => String(value);
    }
    return (value: any) => {
      const s = String(value);
      const parts = s.split("-");
      if (parts.length === 3) return `${parts[1]}/${parts[2]}`;
      return s;
    };
  }, [range]);

  useEffect(() => {
    async function loadAnalytics() {
      if (!user) return;

      try {
        setLoading(true);
        const { summary, courseStats, revenueSeries, completionSeries, topCourseSales } =
          await loadInstructorAnalytics(user.uid, range);
        setStats(summary);
        setCourseStats(courseStats);
        setRevenueSeries(revenueSeries);
        setCompletionSeries(completionSeries);
        setTopCourseSales(topCourseSales);
        setLoading(false);
      } catch (error) {
        console.error("Error loading analytics:", error);
        setLoading(false);
      }
    }

    loadAnalytics();
  }, [user, range]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-textLight">กำลังโหลดข้อมูล...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-20">
      {/* Back Button */}
      <Link to="/instructor/dashboard" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-6 font-semibold transition-colors">
        <ArrowLeft className="w-5 h-5" />
        กลับไปหน้า Dashboard
      </Link>

      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-textDark mb-2">Analytics</h1>
            <p className="text-textLight">ภาพรวมสถิติคอร์สของคุณ • ช่วงเวลา: {rangeLabel}</p>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-textLight">ช่วงเวลา</span>
            <select
              value={range}
              onChange={(e) => setRange(e.target.value as AnalyticsRange)}
              className="px-4 py-2 rounded-xl border-2 border-gray-200 focus:border-primary focus:outline-none transition-colors bg-white"
            >
              <option value="7d">7 วันล่าสุด</option>
              <option value="30d">30 วันล่าสุด</option>
              <option value="90d">90 วันล่าสุด</option>
              <option value="all">ทั้งหมด</option>
            </select>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="จำนวนคอร์ส"
          value={stats.totalCourses}
          icon={<BookOpen className="w-6 h-6" />}
          gradient="from-blue-500 to-cyan-500"
        />
        <StatCard
          title="นักเรียนทั้งหมด"
          value={stats.totalStudents}
          icon={<Users className="w-6 h-6" />}
          gradient="from-blue-500 to-pink-500"
          trend={{ value: "+12%", isPositive: true }}
        />
        <StatCard
          title="รายได้รวม"
          value={`฿${stats.totalRevenue.toLocaleString()}`}
          icon={<DollarSign className="w-6 h-6" />}
          gradient="from-green-500 to-emerald-500"
          trend={{ value: "+23%", isPositive: true }}
        />
        <StatCard
          title="อัตราจบคอร์สเฉลี่ย"
          value={`${stats.avgCompletionRate}%`}
          icon={<Target className="w-6 h-6" />}
          gradient="from-blue-500 to-red-500"
        />
      </div>

      {/* Charts */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-textDark mb-4 flex items-center gap-2">
          <TrendingUp className="w-6 h-6 text-blue-600" />
          Trends
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-textDark">รายได้ ({rangeLabel})</h3>
              <div className="text-sm text-textLight">THB</div>
            </div>

            <div className="h-[280px]">
              {hasRevenueData ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={revenueSeries}
                    margin={{ top: 10, right: 16, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatXAxisLabel as any}
                      minTickGap={12}
                    />
                    <YAxis
                      tickFormatter={(v) => `฿${Number(v).toLocaleString()}`}
                      width={72}
                    />
                    <Tooltip
                      formatter={(value: any) => [
                        `฿${Number(value).toLocaleString()}`,
                        "รายได้",
                      ]}
                      labelFormatter={(label: any) => `วันที่: ${label}`}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="revenue"
                      name="รายได้"
                      stroke="#10B981"
                      strokeWidth={3}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-center px-6">
                  <DollarSign className="w-16 h-16 text-gray-300 mb-2" />
                  <p className="font-semibold text-textDark">ยังไม่มีรายได้ในช่วงเวลานี้</p>
                  <p className="text-sm text-textLight mt-1">
                    ระบบจะนับจากรายการชำระเงินที่มีสถานะ completed
                  </p>
                </div>
              )}
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-textDark">Completion Trend ({rangeLabel})</h3>
              <div className="text-sm text-textLight">%</div>
            </div>

            <div className="h-[280px]">
              {hasCompletionData ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={completionSeries}
                    margin={{ top: 10, right: 16, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatXAxisLabel as any}
                      minTickGap={12}
                    />
                    <YAxis
                      domain={[0, 100]}
                      tickFormatter={(v) => `${v}%`}
                      width={48}
                    />
                    <Tooltip
                      formatter={(value: any) => [`${Number(value)}%`, "อัตราจบคอร์ส"]}
                      labelFormatter={(label: any) => `วันที่: ${label}`}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="completionRate"
                      name="อัตราจบคอร์ส"
                      stroke="#6366F1"
                      strokeWidth={3}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-center px-6">
                  <Target className="w-16 h-16 text-gray-300 mb-2" />
                  <p className="font-semibold text-textDark">ยังไม่มีข้อมูล Completion Trend</p>
                  <p className="text-sm text-textLight mt-1">
                    กราฟนี้ใช้ข้อมูลจาก progress ที่มี completedAt
                  </p>
                </div>
              )}
            </div>
          </Card>
        </div>

        <div className="mt-4">
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-textDark">ยอดขายคอร์สยอดนิยม</h3>
              <div className="text-sm text-textLight">Top 7</div>
            </div>

            <div className="h-[320px]">
              {hasTopSalesData ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={topCourseSales}
                    margin={{ top: 10, right: 16, left: 0, bottom: 24 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis
                      dataKey="name"
                      interval={0}
                      angle={-12}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis
                      yAxisId="left"
                      tickFormatter={(v) => `฿${Number(v).toLocaleString()}`}
                      width={72}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      tickFormatter={(v) => `${v}`}
                      width={48}
                    />
                    <Tooltip
                      formatter={(value: any, name: any) => {
                        if (name === "revenue")
                          return [`฿${Number(value).toLocaleString()}`, "รายได้"];
                        return [Number(value), "ยอดขาย"];
                      }}
                    />
                    <Legend />
                    <Bar
                      yAxisId="left"
                      dataKey="revenue"
                      name="รายได้"
                      fill="#06B6D4"
                      radius={[8, 8, 0, 0]}
                    />
                    <Bar
                      yAxisId="right"
                      dataKey="sales"
                      name="ยอดขาย"
                      fill="#EC4899"
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-center px-6">
                  <BookOpen className="w-16 h-16 text-gray-300 mb-2" />
                  <p className="font-semibold text-textDark">ยังไม่มีข้อมูลยอดขายในช่วงเวลานี้</p>
                  <p className="text-sm text-textLight mt-1">
                    ระบบจะแสดง Top คอร์สเมื่อมีรายการชำระเงิน completed
                  </p>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* Course Performance */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-textDark mb-4">
          ประสิทธิภาพแต่ละคอร์ส
        </h2>

        {courseStats.length === 0 ? (
          <Card>
            <p className="text-textLight text-center py-8">
              ยังไม่มีคอร์ส เริ่มสร้างคอร์สแรกของคุณเลย!
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            {courseStats.map((course) => (
              <Card key={course.id}>
                <div className="flex items-center gap-4">
                  <img
                    src={course.thumbnail}
                    alt={course.title}
                    className="w-20 h-20 rounded-xl object-cover"
                  />
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-textDark mb-2">
                      {course.title}
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-textLight">นักเรียน</p>
                        <p className="font-semibold text-textDark">
                          {course.totalStudents} คน
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-textLight">รายได้</p>
                        <p className="font-semibold text-success">
                          ฿{course.totalRevenue.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-textLight">บทเรียน</p>
                        <p className="font-semibold text-textDark">
                          {course.totalLessons} บท
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-textLight">อัตราจบคอร์ส</p>
                        <p className="font-semibold text-primary">
                          {course.completionRate}%
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mt-4">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-gradient-primary h-2 rounded-full transition-all duration-500"
                      style={{ width: `${course.completionRate}%` }}
                    ></div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Top Courses */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-textDark mb-4 flex items-center gap-2">
          <Trophy className="w-6 h-6 text-yellow-500" />
          Top Courses
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <h3 className="font-bold text-textDark mb-4">Top รายได้</h3>
            {stats.topCoursesByRevenue.length === 0 ? (
              <p className="text-sm text-textLight">ยังไม่มีข้อมูลรายได้ในช่วงเวลานี้</p>
            ) : (
              <div className="space-y-3">
                {stats.topCoursesByRevenue.map((c, idx) => (
                  <div key={c.courseId} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-primary text-white flex items-center justify-center font-bold">
                        {idx + 1}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-textDark truncate max-w-[260px]">
                          {c.title}
                        </p>
                      </div>
                    </div>
                    <p className="font-bold text-success">฿{c.revenue.toLocaleString()}</p>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card>
            <h3 className="font-bold text-textDark mb-4">Top นักเรียน</h3>
            {stats.topCoursesByStudents.length === 0 ? (
              <p className="text-sm text-textLight">ยังไม่มีข้อมูลนักเรียนในช่วงเวลานี้</p>
            ) : (
              <div className="space-y-3">
                {stats.topCoursesByStudents.map((c, idx) => (
                  <div key={c.courseId} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-pink-600 text-white flex items-center justify-center font-bold">
                        {idx + 1}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-textDark truncate max-w-[260px]">
                          {c.title}
                        </p>
                      </div>
                    </div>
                    <p className="font-bold text-textDark">{c.students} คน</p>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Insights */}
      <div>
        <h2 className="text-2xl font-bold text-textDark mb-4 flex items-center gap-2">
          <Lightbulb className="w-6 h-6 text-yellow-500" />
          Insights
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-gradient-primary rounded-lg flex items-center justify-center text-white flex-shrink-0">
                <Rocket className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-bold text-textDark mb-1">
                  เพิ่มบทเรียนใหม่
                </h3>
                <p className="text-sm text-textLight">
                  คอร์สที่มีบทเรียนมากกว่า 10 บท มีอัตราจบคอร์สสูงกว่า 30%
                </p>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-gradient-accent rounded-lg flex items-center justify-center text-white flex-shrink-0">
                <TrendingUp className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-bold text-textDark mb-1">
                  อัปเดตเนื้อหา
                </h3>
                <p className="text-sm text-textLight">
                  คอร์สที่อัปเดตเนื้อหาสม่ำเสมอมีนักเรียนเพิ่มขึ้น 45%
                </p>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-gradient-soft rounded-lg flex items-center justify-center flex-shrink-0">
                <Target className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-bold text-textDark mb-1">
                  เพิ่มแบบฝึกหัด
                </h3>
                <p className="text-sm text-textLight">
                  คอร์สที่มีแบบฝึกหัดมีอัตราจบคอร์สสูงกว่า 25%
                </p>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center text-white flex-shrink-0">
                <MessageCircle className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-bold text-textDark mb-1">
                  ตอบคำถามนักเรียน
                </h3>
                <p className="text-sm text-textLight">
                  การตอบคำถามภายใน 24 ชม. เพิ่มความพึงพอใจ 60%
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
